import './theme/huiFonts.css';
import './theme/theme.css';

import { BrowserRouter } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { HammerUIProvider } from '@liveauctioneers/hammer-ui-core/providers';
import { HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { IStyleSheetContext, StyleSheetManager, ThemeProvider } from 'styled-components';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { store } from '@/redux/store';
import App from './pages/App/App';
import en from './intl/en';
import ErrorBoundary from '@liveauctioneers/caterwaul-components/lib/ErrorBoundary/ErrorBoundary';
import flattenMessages from '@liveauctioneers/caterwaul-components/lib/utils/flattenMessages';
import isPropValid from '@emotion/is-prop-valid';
import React from 'react';
import './polyfills';

const THREE_HOURS = 3 * 60 * 60 * 1000;

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            initialDataUpdatedAt: Date.now() / 1000,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: THREE_HOURS,
        },
    },
});

/**
 * When we migrate completely to hammer-ui inputs and typography, we should be able to delete this prop filtering
 *
 * This implements the default behavior from styled-components v5
 * @see https://styled-components.com/docs/faqs#shouldforwardprop-is-no-longer-provided-by-default
 */
const shouldForwardProp: IStyleSheetContext['shouldForwardProp'] = (propName, target) => {
    if (typeof target === 'string') {
        // For HTML elements, forward the prop if it is a valid HTML attribute
        return isPropValid(propName);
    }
    // For other elements, forward all props
    return true;
};

const renderTheApp = () => {
    const target = document.querySelector('#root');
    const root = createRoot(target);

    if (target) {
        root.render(
            <QueryClientProvider client={queryClient}>
                <HammerUIProvider theme={mergedCaterwaulAndHammerUIThemes}>
                    <ReactQueryDevtools initialIsOpen={false} />
                    <ErrorBoundary onError={(e) => console.error(e)}>
                        <ThemeProvider theme={mergedCaterwaulAndHammerUIThemes}>
                            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                                <HelmetProvider>
                                    <Provider store={store}>
                                        <IntlProvider
                                            locale="en"
                                            messages={flattenMessages(en)}
                                        >
                                            <React.StrictMode>
                                                <BrowserRouter
                                                    future={{ v7_relativeSplatPath: false, v7_startTransition: false }}
                                                >
                                                    <App />
                                                </BrowserRouter>
                                            </React.StrictMode>
                                        </IntlProvider>
                                    </Provider>
                                </HelmetProvider>
                            </StyleSheetManager>
                        </ThemeProvider>
                    </ErrorBoundary>
                </HammerUIProvider>
            </QueryClientProvider>
        );
    }
};

renderTheApp();
